import React, { useEffect, useState } from "react";
import { Table, Button, message, Spin, Alert, Row, Col, Input, Select, Pagination, Typography, Grid, Card, Tag } from "antd";
import ApprovalActionModal from "../../components/ApprovalActionModal";
import { fetchApprovalInstance, progressApprovalInstance, fetchBillById } from "../../api";
import { useAuth } from "../../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import ApprovalInstanceView from "./ApprovalInstanceView";
import ViewCreatedBill from "../../../billing/pages/ViewCreatedBill";
import { CheckCircleTwoTone, CloseCircleTwoTone, SearchOutlined } from "@ant-design/icons";
import { usePermissions } from "../../../../hooks/usePermissions";
import { ActionType, ModuleType, PermissionButton } from "../../../../components";
import "bootstrap/dist/css/bootstrap.min.css";

const { Option } = Select;
const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

const STATUS_OPTIONS = [
  { label: "Pending", value: "pending" },
  { label: "Completed", value: "completed" },
  { label: "Rejected", value: "rejected" },
  { label: "Escalated", value: "escalated" },
];

const PAGE_SIZE = 10;

const ApprovalInstanceListViewer = () => {
  const { user } = useAuth();
  const { canViewModule, canViewField } = usePermissions();
  const module = ModuleType.APPROVALS; // Assuming APPROVAL module exists, adjust as needed
  const userId = user?.id || user?._id;

  const [approvals, setApprovals] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: PAGE_SIZE });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    search: "",
    status: "pending",
    page: 1,
    limit: PAGE_SIZE,
  });

  const [modal, setModal] = useState({
    visible: false,
    actionType: null,
    instance: null,
    comment: "",
    loading: false,
  });

  const navigate = useNavigate();
  const [viewInstance, setViewInstance] = useState(null);
  const [viewBill, setViewBill] = useState(null);
  const screens = useBreakpoint();
  const [messageApi, contextHolder] = message.useMessage();

  const fetchApprovals = async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchApprovalInstance({
        userId,
        ...filters,
        ...params,
      });
      setApprovals(response.data || []);
      setMeta(response.meta || { total: 0, page: 1, limit: PAGE_SIZE });
    } catch (err) {
      setError("Failed to fetch approvals");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (userId) fetchApprovals();
    // eslint-disable-next-line
  }, [userId, filters.page, filters.status, filters.search]);

  const handleAction = (instance, actionType) => {
    setModal({
      visible: true,
      actionType,
      instance,
      comment: "",
      loading: false,
    });
  };

  // `payload` is the decision-time input the modal collected (bank UTRs for a
  // payment approval); undefined for every request that needs none.
  const handleModalOk = async (payload) => {
    if (!modal.comment.trim()) {
      messageApi.warning("Comment is required");
      return;
    }
    setModal((m) => ({ ...m, loading: true }));
    try {
      await progressApprovalInstance({
        instanceId: modal.instance._id,
        action: modal.actionType,
        comment: modal.comment,
        payload,
      });
      setModal({ visible: false, actionType: null, instance: null, comment: "", loading: false });
      messageApi.success("Action successful");
      fetchApprovals();
    } catch (err) {
      setModal((m) => ({ ...m, loading: false }));
      messageApi.error(err?.response?.data?.message || "Failed to progress approval");
    }
  };

  const handleModalCancel = () => {
    setModal({ visible: false, actionType: null, instance: null, comment: "", loading: false });
  };

  const handleObjectIdClick = async (record) => {
    if (record.module === "Bill" || record.module === "Billing") {
      try {
        setLoading(true);
        const bill = await fetchBillById(record.objectId);
        // Pass approval instance and source flag to the bill view
        setViewBill({ data: bill, approvalInstance: record, fromApprovals: true });
      } catch {
        messageApi.error("Failed to fetch bill details");
      } finally {
        setLoading(false);
      }
    } else {
      setViewInstance(record);
    }
  };

  const formatDateTime12hr = (date) => {
    if (!date) return "-";
    const d = new Date(date);
    return d.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleFilterChange = (changed) => {
    setFilters((prev) => ({
      ...prev,
      ...changed,
      page: 1, // Reset to first page on filter/search change
    }));
  };

  const handlePageChange = (page, pageSize) => {
    setFilters((prev) => ({
      ...prev,
      page,
      limit: pageSize,
    }));
  };

  // Check module permission early
  if (!canViewModule(module)) {
    return (
      <div className="container" style={{ marginTop: 32 }}>
        <div className="text-center mt-5">
          <Text type="danger">You do not have permission to view approvals.</Text>
        </div>
      </div>
    );
  }

  const columns = [
    {
      title: "Module",
      dataIndex: "module",
      key: "module",
      render: (text) => canViewField(module, 'module') ? text : '***',
      responsive: ['xs', 'sm', 'md', 'lg'],
    },
    {
      title: "Object ID",
      dataIndex: "objectId", 
      key: "objectId",
      render: (objectId, record) => {
        if (!canViewField(module, 'objectId')) return '***';
        return (
          <Button
            type="link"
            onClick={() => handleObjectIdClick(record)}
            style={{ padding: 0 }}
          >
            {objectId}
          </Button>
        );
      },
      responsive: ['xs', 'sm', 'md', 'lg'],
    },
    {
      title: "Step Name",
      dataIndex: ["steps"],
      key: "stepName",
      render: (steps, record) => {
        if (!canViewField(module, 'stepName')) return '***';
        const step = steps?.find((s) => s.stepNumber === record.currentStep);
        return step?.stepName || "-";
      },
      responsive: ['sm', 'md', 'lg'],
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => canViewField(module, 'status') ? status?.toUpperCase() : '***',
      responsive: ['xs', 'sm', 'md', 'lg'],
    },
    {
      title: "Deadline",
      dataIndex: ["steps"],
      key: "deadline",
      render: (steps, record) => {
        if (!canViewField(module, 'deadline')) return '***';
        const step = steps?.find((s) => s.stepNumber === record.currentStep);
        return step?.deadline ? formatDateTime12hr(step.deadline) : "-";
      },
      responsive: ['md', 'lg'],
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy",
      render: (createdBy) => {
        if (!canViewField(module, 'createdBy')) return '***';
        return createdBy?.name ? createdBy.name : createdBy || "-";
      },
      responsive: ['md', 'lg'],
    },
    {
      title: "Created At",
      dataIndex: "startedAt",
      key: "startedAt",
      render: (startedAt) => {
        if (!canViewField(module, 'createdAt')) return '***';
        return startedAt ? formatDateTime12hr(startedAt) : "-";
      },
      responsive: ['md', 'lg'],
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => {
        const status = String(record.status || "").toLowerCase();
        // Completed / rejected states show an indicator
        if (status === "completed") {
          return (
            <span style={{ color: "#52c41a", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
              <CheckCircleTwoTone twoToneColor="#52c41a" /> Completed
            </span>
          );
        }
        if (status === "rejected") {
          return (
            <span style={{ color: "#ff4d4f", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
              <CloseCircleTwoTone twoToneColor="#ff4d4f" /> Rejected
            </span>
          );
        }

        // Determine if the current user is an approver for the active step
        const step = record.steps?.find((s) => s.stepNumber === record.currentStep);
        const stepStatus = String(step?.status || "").toLowerCase();
        const isPendingWithUser = status === 'pending' && step && stepStatus === 'pending' && Array.isArray(step.approvers) && step.approvers.some(a => String(a) === String(userId));

        // If not pending with this user, hide approve/reject buttons
        if (!isPendingWithUser) {
          return <Text type="secondary">Pending</Text>;
        }

        return (
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <PermissionButton
              buttonType={ActionType.APPROVE}
              module={module}
              type="primary"
              size="small"
              style={{
                background: "linear-gradient(90deg, #36d1c4 0%, #5b86e5 100%)",
                border: "none",
                fontWeight: 500,
                borderRadius: 6,
                boxShadow: "0 2px 8px rgba(91,134,229,0.08)",
                minWidth: 80,
              }}
              onClick={() => handleAction(record, "approved")}
              icon={<CheckCircleTwoTone twoToneColor="#fff" />}
            >
              Approve
            </PermissionButton>
            <PermissionButton
              buttonType={ActionType.REJECT}
              module={module}
              danger
              size="small"
              style={{
                background: "linear-gradient(90deg, #ff5858 0%, #f09819 100%)",
                color: "#fff",
                border: "none",
                fontWeight: 500,
                borderRadius: 6,
                minWidth: 80,
              }}
              onClick={() => handleAction(record, "rejected")}
              icon={<CloseCircleTwoTone twoToneColor="#fff" />}
            >
              Reject
            </PermissionButton>
          </div>
        );
      },
      responsive: ['xs', 'sm', 'md', 'lg'],
    },
  ];

  if (viewBill) {
    return <ViewCreatedBill bill={viewBill} />;
  }

  if (viewInstance) {
    return (
      <ApprovalInstanceView
        instanceId={viewInstance._id}
        module={viewInstance.module}
      />
    );
  }

  // Custom modal message for approve/reject
  const getActionModalTitle = () => {
    if (modal.actionType === "approved") return "Approve this request?";
    if (modal.actionType === "rejected") return "Reject this request?";
    return "Action";
  };

  return (
    <div className="container-fluid" style={{ marginTop: 24 }}>
      {contextHolder}
      <Card>
        <Row justify="space-between" align="middle" className="mb-3">
          <Col>
            <Title level={2} style={{ margin: 0 }}>Pending Approvals</Title>
          </Col>
        </Row>

        <Row gutter={[16, 16]} className="mb-3">
          <Col xs={24} md={10}>
            <Input.Search
              placeholder="Search by Object ID or Step Name"
              allowClear
              value={filters.search}
              onChange={e => handleFilterChange({ search: e.target.value })}
              onSearch={val => handleFilterChange({ search: val })}
              enterButton={<SearchOutlined />}
              size={screens.xs ? 'small' : 'middle'}
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              value={filters.status}
              onChange={val => handleFilterChange({ status: val })}
              style={{ width: "100%" }}
              size={screens.xs ? 'small' : 'middle'}
            >
              {STATUS_OPTIONS.map(opt => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
            <Spin spinning={loading}>
              {screens.md ? (
                <Table
                  columns={columns}
                  dataSource={approvals}
                  rowKey="_id"
                  pagination={false}
                  bordered
                  style={{ background: "#fff" }}
                  locale={{ emptyText: "No approvals found matching the criteria." }}
                  scroll={{ x: 'max-content' }}
                  size={screens.lg ? 'middle' : 'small'}
                />
              ) : (
                <div>
                  {approvals && approvals.length ? (
                    approvals.map((item) => (
                      <Card key={item._id} size="small" hoverable style={{ marginBottom: 12 }}>
                        <Row justify="space-between" align="middle">
                          <Col span={16}>
                            <Title level={5} style={{ marginBottom: 0 }}>{item.module}</Title>
                            <Text type="secondary" style={{ display: 'block' }}>{item.objectId}</Text>
                          </Col>
                          <Col span={8} style={{ textAlign: 'right' }}>
                            <Text type="secondary">{(item.steps?.find(s => s.stepNumber === item.currentStep)?.stepName) || '-'}</Text>
                            <div>
                              <Text type="secondary">{(item.steps?.find(s => s.stepNumber === item.currentStep)?.deadline) ? formatDateTime12hr(item.steps.find(s => s.stepNumber === item.currentStep).deadline) : '-'}</Text>
                            </div>
                          </Col>
                        </Row>
                        <div style={{ marginTop: 8, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                          {(() => {
                            const status = String(item.status || '').toLowerCase();
                            const step = item.steps?.find(s => s.stepNumber === item.currentStep);
                            const stepStatus = String(step?.status || '').toLowerCase();
                            const isPendingWithUser = status === 'pending' && step && stepStatus === 'pending' && Array.isArray(step.approvers) && step.approvers.some(a => String(a) === String(userId));
                            if (isPendingWithUser) {
                              return (
                                <>
                                  <PermissionButton
                                    buttonType={ActionType.APPROVE}
                                    module={module}
                                    type="primary"
                                    size="small"
                                    onClick={() => handleAction(item, 'approved')}
                                  >
                                    Approve
                                  </PermissionButton>
                                  <PermissionButton
                                    buttonType={ActionType.REJECT}
                                    module={module}
                                    danger
                                    size="small"
                                    onClick={() => handleAction(item, 'rejected')}
                                  >
                                    Reject
                                  </PermissionButton>
                                </>
                              );
                            }
                            return <Text type="secondary" style={{ alignSelf: 'center' }}>Pending</Text>;
                          })()}
                          <Button size="small" type="link" onClick={() => handleObjectIdClick(item)}>Open</Button>
                        </div>
                      </Card>
                    ))
                  ) : (
                    <div style={{ textAlign: 'center', padding: 24 }}>
                      <Text type="secondary">No approvals found matching the criteria.</Text>
                    </div>
                  )}
                </div>
              )}

              <Pagination
                style={{ marginTop: 16, textAlign: "right" }}
                current={meta.page}
                pageSize={meta.limit}
                total={meta.total}
                onChange={handlePageChange}
                showSizeChanger
                pageSizeOptions={["5", "10", "20", "50"]}
                size={screens.xs ? 'small' : 'default'}
                responsive
              />
            </Spin>
          </Col>
        </Row>
      </Card>

      <ApprovalActionModal
        visible={modal.visible}
        actionType={modal.actionType}
        comment={modal.comment}
        onChange={(e) => setModal((m) => ({ ...m, comment: e.target.value }))}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        loading={modal.loading}
        instanceId={modal.instance?._id}
        title={getActionModalTitle()}
        okText={modal.actionType === "approved" ? "Approve" : modal.actionType === "rejected" ? "Reject" : "OK"}
        cancelText="Cancel"
        description={
          modal.actionType === "approved"
            ? "Are you sure you want to approve this request? Please add a comment."
            : modal.actionType === "rejected"
            ? "Are you sure you want to reject this request? Please add a comment."
            : ""
        }
      />
    </div>
  );
};

export default ApprovalInstanceListViewer;
